import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { VerificationPackageCheckType } from '../../organizations/services/organizations.service';
import { VerificationPackage, VerificationPackageDetail } from '../models/VerificationPackage';
import { GuestVerificationCheckType } from '../../guests/models/Guest';
import { VerificationPackageCheckTypeByIdResponse } from '../models/VerificationCheckType';
import { VerificationPartner } from '../models/VerificationPartner';
import { AdminPortalAPIHttpClient } from '../../../infrastructure/http/v2/AdminPortalAPIHttpClient';
import { isStringEmptyOrNull } from '../../../utilities/StringUtility';

@Injectable({
  providedIn: 'root'
})
export class VerificationPackagesService {

  private verificationPackageCheckTypes: VerificationPackageCheckType = {};

  constructor(
    private adminPortalHttpClient: AdminPortalAPIHttpClient,
    private snackbar: MatSnackBar
  ) { }

  async getVerificationPackages(): Promise<VerificationPackageDetail[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/verification-packages`));
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving verification packages: ${JSON.stringify(e)}`);
    }
  }

  async getVerificationPackageDetail(verificationPackageId: string): Promise<VerificationPackageDetail> {
    const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/verification-packages/${verificationPackageId}`));
    return Promise.resolve(response?.data);
  }

  async getVerificationPackagesByOrgId(organizationId?: number): Promise<VerificationPackage[]> {
    if (!organizationId) {
      return Promise.reject(`Null or undefined organization id`);
    }

    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/organizations/verification-packages?organization_id=${organizationId}`));
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving organization verification package details for id ${organizationId} ${JSON.stringify(e)}`);
    }
  }

  async createVerificationPackage(payload: any): Promise<VerificationPackage> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.post(`/verification-packages`, payload));
      if (!response) {
        return Promise.reject(`Create verification package response null`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return response.data;
    } catch (e) {
      return Promise.reject(`Error while creating verification package: ${e}`);
    }
  }

  async updateVerificationPackage(verificationPackageId: any, verificationPackage: any): Promise<VerificationPackage> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.put(`/verification-packages/${verificationPackageId}`, verificationPackage));
      if (!response) {
        return Promise.reject(`Update verification package response null ${verificationPackageId}`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while updating verification package ${verificationPackageId}: ${e}`);
    }
  }

  async updateVerificationPackageStatus(verificationPackageId: any, verificationPackage: any): Promise<VerificationPackage> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.put(`/verification-packages/update-status/${verificationPackageId}`, verificationPackage));
      if (!response) {
        return Promise.reject(`Update verification package response null ${verificationPackageId}`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while updating verification package ${verificationPackageId}: ${e}`);
    }
  }

  async getVerificationCheckTypes(): Promise<any[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/reference/verification-check-types`));
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving Verification Check Types: ${JSON.stringify(e)}`);
    }
  }

  async getVerificationPackageCheckTypesById(verificationPackageId: number): Promise<VerificationPackageCheckTypeByIdResponse[]> {
    if (!verificationPackageId) {
      return Promise.reject(`Null or undefined verification package id`);
    }

    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/verification-packages/check-types?verification_package_id=${verificationPackageId}`));
      if (!response || !response.data) {
        return Promise.reject(`Get verification package check types response null for package id ${verificationPackageId}`);
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving verification package check types for package id ${verificationPackageId} ${e}}`);
    }
  }

  async getOrgVerificationPackageCheckTypesById(verificationPackageId: number): Promise<any[]> {
    if (!verificationPackageId) {
      return Promise.reject(`Null or undefined verification package id`);
    }

    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/organizations/verification-packages/check-types?verification_package_id=${verificationPackageId}`));
      if (!response || !response.data) {
        return Promise.reject(`Get verification package check types response null for package id ${verificationPackageId}`);
      }

      const availableCheckTypes = Object.values(GuestVerificationCheckType);
      const checkTypes: GuestVerificationCheckType[] = response.data.map((check: any) => {
        return availableCheckTypes.includes(check.check_type.display_text) ? check.check_type.display_text : null;
      }).filter((check: GuestVerificationCheckType) => check);

      this.verificationPackageCheckTypes[verificationPackageId] = checkTypes;

      return Promise.resolve(checkTypes);
    } catch (e) {
      return Promise.reject(`Error while retrieving verification package check types for package id ${verificationPackageId} ${e}}`);
    }
  }

  async createVerificationCheckType(payload: any): Promise<VerificationPackageCheckType> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.post(`/verification-packages/check-types`, payload));
      if (!response) {
        return Promise.reject(`Create verification check type response null`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return response.data;
    } catch (e) {
      return Promise.reject(`Error while creating verification check type: ${e}`);
    }
  }

  async deleteVerificationPackageType(verificationPackageTypeId: number): Promise<VerificationPackageCheckType> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.delete(`/verification-packages/check-types/delete/${verificationPackageTypeId}`));
      if (!response) {
        return Promise.reject(`Delete verification package type response null`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while deleting verification package: ${e}`);
    }
  }

  async getVerificationPartners(): Promise<VerificationPartner[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/reference/verification-partners`));
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving Verification Partners: ${JSON.stringify(e)}`);
    }
  }


  displaySnackbar(errorMessage: string) {
    if (isStringEmptyOrNull(errorMessage)) {
      this.displaySuccessSnackbar();
    } else {
      this.displayErrorSnackbar(errorMessage);
    }
  }
    
  displaySuccessSnackbar() {
    this.snackbar.open('Save Successful', 'OK', { duration: 4000 });
  }

  displayErrorSnackbar(errorMessage: string) {
    this.snackbar.open(`Error Saving Policy: ${errorMessage}`, 'OK', { duration: 4000 });
  }
}